
import { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  Upload,
  Mail,
  Bell,
  Lock,
} from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

export default function SettingsPage() {
  const [settings, setSettings] = useState({
    logo: "",
    name: "",
    email: "",
    emailNotif: false,
    systemNotif: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem("appSettings");
    if (stored) {
      setSettings(JSON.parse(stored));
    }
  }, []);

  const updateField = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleSave = () => {
    localStorage.setItem("appSettings", JSON.stringify(settings));
    toast.success("Settings Saved Successfully!");
  };

  return (
    <div className="min-h-screen  bg-gray-50">
      <div className=" mx-auto space-y-8">

        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-serif text-gray-800">
          Settings Page
        </h1>
          <p className="text-gray-500 text-sm mt-1">
            Refine your experience and manage application preferences.
          </p>
        </div>

        {/* General */}
        <section className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              General
            </h2>
            <p className="text-sm text-gray-500">
              Manage basic application settings
            </p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-sm text-gray-600">App Name</label>
              <input
                type="text"
                value="KL Property"
                readOnly
                className="w-full mt-1 bg-gray-100 border border-gray-200 rounded-lg p-2.5 text-sm"
              />
            </div>

            {/* Logo */}
            <div>
              <label className="text-sm text-gray-600">
                Logo Upload
              </label>

              <div className="flex items-center gap-4 mt-2">
                <div className="w-20 h-20 bg-green-100 rounded-xl flex items-center justify-center overflow-hidden text-gray-400 text-2xl">
                  {settings.logo ? (
                    <img
                      src={settings.logo}
                      alt="App Logo"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    "🖼"
                  )}
                </div>

                <label className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm cursor-pointer transition">
                  <Upload size={16} />
                  Change Logo
                  <input
                    type="file"
                    hidden
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      const base64 = await convertToBase64(file);
                      updateField("logo", base64);
                    }}
                  />
                </label>
              </div>

              <p className="text-xs text-gray-400 mt-1">
                PNG or SVG, max 2MB.
              </p>
            </div>
          </div>
        </section>

        {/* Account */}
        <section className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800">
            Account
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-600">
                Full Name
              </label>
              <input
                type="text"
                value={settings.name}
                onChange={(e) => updateField("name", e.target.value)}
                className="w-full mt-1 bg-gray-100 border border-gray-200 rounded-lg p-2.5 text-sm"
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">
                Email Address
              </label>
              <input
                type="email"
                value={settings.email}
                onChange={(e) => updateField("email", e.target.value)}
                className="w-full mt-1 bg-gray-100 border border-gray-200 rounded-lg p-2.5 text-sm"
              />
            </div>
          </div>

          {/* Change Password */}
          <button className="flex items-center gap-2 text-green-600 text-sm font-medium hover:underline">
            <Lock size={16} />
            Change Password
          </button>
        </section>

        {/* Notifications */}
        <section className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-800">
            Notifications
          </h2>

          <div className="space-y-4">
            {/* Email */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm">
                <Mail size={18} className="text-gray-500" />
                <div>
                  <p className="font-medium text-gray-700">
                    Email Notifications
                  </p>
                  <p className="text-xs text-gray-400">
                    Get emails about activity
                  </p>
                </div>
              </div>

              <input
                type="checkbox"
                checked={settings.emailNotif}
                onChange={(e) =>
                  updateField("emailNotif", e.target.checked)
                }
                className="accent-green-600 w-5 h-5"
              />
            </div>

            {/* System */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-sm">
                <Bell size={18} className="text-gray-500" />
                <div>
                  <p className="font-medium text-gray-700">
                    System Notifications
                  </p>
                  <p className="text-xs text-gray-400">
                    Real-time browser alerts
                  </p>
                </div>
              </div>

              <input
                type="checkbox"
                checked={settings.systemNotif}
                onChange={(e) =>
                  updateField("systemNotif", e.target.checked)
                }
                className="accent-green-600 w-5 h-5"
              />
            </div>
          </div>
        </section>

        {/* Save */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full text-sm font-medium shadow-sm transition"
          >
            Save Changes
          </button>
        </div>

        <ToastContainer position="top-right" autoClose={2000} />
      </div>
    </div>
  );
}
